/**
 * Arabic for the flight layer. Proposed; pending native aviation review.
 *
 * Three terms carry the whole thing and are kept as far apart as the English
 * keeps them, because the distinction is the point:
 *
 *   scheduled  المُجدوَل   — what the schedule says
 *   estimated  المتوقَّع    — somebody's belief
 *   actual     الفعلي      — what happened
 *
 * Aerodrome code letters (A–F), ICAO type designators (A320, B77W) and
 * airport codes (OEJN) are left as written: they are international and a
 * translated code is an unreadable one. IATA delay codes stay as digits.
 */
const ar: Record<string, string> = {
  /* the suite and its screens */
  "Flight operations": "عمليات الطيران",
  "{site} · Flight operations": "{site} · عمليات الطيران",
  "Today's flights": "رحلات اليوم",
  "Stands and gates": "المواقف والبوابات",
  "The day's movements and the stands they use — what flew, what is late and why, and where every aircraft parks.":
    "حركات اليوم والمواقف التي تستخدمها — ما طار، وما تأخر ولماذا، وأين تقف كل طائرة.",

  /* the three times */
  Estimated: "المتوقَّع",
  Route: "المسار",
  Aircraft: "الطائرة",
  Delay: "التأخير",
  "Delay code": "رمز التأخير",
  Both: "الاثنان",
  "Arrivals or departures": "الوصول أو المغادرة",
  "not updated": "لم يُحدَّث",
  "unidentified movement": "حركة غير محددة",
  "another movement": "حركة أخرى",

  /* punctuality */
  "On time": "في الموعد",
  Early: "مبكر",
  Late: "متأخر",
  "Not known": "غير معروف",
  "Late only": "المتأخرة فقط",
  "Late with no reason": "المتأخرة بلا سبب",
  "Still to come": "لم تأت بعد",
  "on time": "في الموعد",
  "of {n} measured": "من {n} مقيسة",
  "of {n} late": "من {n} متأخرة",
  "no code": "بلا رمز",
  "no delay code recorded": "لم يُسجَّل رمز تأخير",
  "not attributable": "غير قابل للإسناد",
  "late with no reason recorded": "متأخرة بلا سبب مسجل",
  "every late movement carries a delay code": "كل حركة متأخرة تحمل رمز تأخير",
  "Every late movement carries a delay code.": "كل حركة متأخرة تحمل رمز تأخير.",
  "Nothing has moved yet, so there is no punctuality to report.": "لم تتحرك أي رحلة بعد، فلا انضباط مواعيد يُذكر.",
  "{pct}% of {n} on time.": "{pct}% من {n} في الموعد.",
  "{n} late movement carries no delay code, so the reason for it is not on the record.|{n} late movements carry no delay code, so the reasons for them are not on the record.":
    "كل المتأخرات تحمل رمزاً.|حركة متأخرة واحدة بلا رمز تأخير، فسببها ليس في السجل.|حركتان متأخرتان بلا رمز تأخير، فسببهما ليس في السجل.|{n} حركات متأخرة بلا رمز تأخير، فأسبابها ليست في السجل.|{n} حركة متأخرة بلا رمز تأخير، فأسبابها ليست في السجل.|{n} حركة متأخرة بلا رمز تأخير، فسببها ليس في السجل.",
  "Nothing is late.": "لا شيء متأخر.",
  "Nothing is canceled.": "لا شيء ملغى.",
  "Nothing is scheduled.": "لا شيء مُجدوَل.",
  "Where the delay went": "أين ذهب التأخير",
  "by the code somebody entered, uninterpreted": "حسب الرمز الذي أدخله أحدهم، دون تفسير",

  /* the day */
  "movements today": "حركات اليوم",
  "{a} arrivals, {d} departures": "{a} وصول، و{d} مغادرة",
  "have happened": "تمت فعلاً",
  "{n} still to come": "{n} لم تأت بعد",
  passengers: "المسافرون",
  "{n} movements file no passenger figure": "{n} حركات لا تُدرج عدد مسافرين",
  "every movement files a figure": "كل حركة تُدرج رقماً",
  "busiest hour": "أكثر الساعات ازدحاماً",
  "nothing scheduled": "لا شيء مُجدوَل",
  "nothing has moved yet": "لم تتحرك أي رحلة بعد",
  "{h}:00 — {a} in, {d} out": "{h}:00 — {a} قادمة، و{d} مغادرة",
  "{h}:00 is the busiest hour: {a} arrivals and {d} departures.":
    "{h}:00 هي أكثر الساعات ازدحاماً: {a} وصول و{d} مغادرة.",
  "The day, hour by hour": "اليوم، ساعة بساعة",
  "where the banks fall, and where the estate is under pressure": "أين تقع موجات الحركة، وأين تقع المرافق تحت الضغط",
  "Arrivals above, departures below. A movement sits in the hour it actually happened, not the hour it was due.":
    "الوصول أعلى، والمغادرة أسفل. وتوضع الحركة في الساعة التي حدثت فيها فعلاً، لا الساعة التي كانت مقررة لها.",
  "The movements": "الحركات",
  "scheduled, estimated and actual, each labelled as what it is":
    "المُجدوَل والمتوقَّع والفعلي، كل منها مُعلَّم بما هو",
  "{n} movement is cancelled.|{n} movements are canceled.":
    "لا إلغاءات.|حركة واحدة ملغاة.|حركتان ملغاتان.|{n} حركات ملغاة.|{n} حركة ملغاة.|{n} حركة ملغاة.",
  "{site}: {a} arrivals and {d} departures today, {moved} of them flown.":
    "{site}: {a} وصول و{d} مغادرة اليوم، طار منها {moved}.",
  "Every movement of the operating day, arrivals and departures kept apart because they are separately delayed and separately parked. Scheduled, estimated and actual are three different times and the board says which one it is showing — an estimate is somebody's belief, an actual is the only one that is evidence.":
    "كل حركة في يوم التشغيل، والوصول والمغادرة منفصلان لأنهما يتأخران منفصلين ويقفان منفصلين. والمُجدوَل والمتوقَّع والفعلي ثلاثة أوقات مختلفة، واللوحة تقول أيها تعرض — فالمتوقَّع اعتقاد أحدهم، والفعلي وحده دليل.",
  "Entered, not read from a schedule feed. The platform holds the movement tables but publishes no endpoint for them yet, so this is held here and shaped to become a read of the platform's own without this screen changing. A forecast row is never added to today's totals.":
    "مُدخل يدوياً ولا يُقرأ من تغذية جداول. المنصة تحتفظ بجداول الحركة لكنها لا تنشر نقطة وصول لها بعد، فيُحفظ هذا هنا بشكل يسمح بأن يصير قراءة من المنصة دون تغيير هذه الشاشة. وصف التوقع لا يُضاف أبداً إلى إجماليات اليوم.",
  Cargo: "شحن",
  Ferry: "تموضع فارغ",
  "General aviation": "طيران عام",
  "State or military": "حكومي أو عسكري",
  Positioning: "تموضع",

  /* the stands */
  "Contact stand": "موقف ملاصق",
  "Remote stand": "موقف بعيد",
  "Cargo stand": "موقف شحن",
  "De-icing": "إزالة الجليد",
  Takes: "يستوعب",
  "code {c}": "الرمز {c}",
  "air bridge": "جسر ركاب",
  "On it today": "عليه اليوم",
  "(planned)": "(مخطط)",
  "(there now)": "(موجودة الآن)",
  "{n} usable today": "{n} صالحة اليوم",
  "closed by other work": "مغلقة بأعمال أخرى",
  "open work orders and closures, read from the platform": "أوامر عمل مفتوحة وإغلاقات، مقروءة من المنصة",
  "nothing is closed": "لا شيء مغلق",
  "closures and work orders that name the stand, read from the platform":
    "إغلاقات وأوامر عمل تسمي الموقف، مقروءة من المنصة",
  "stands with work on their apron": "مواقف عليها أعمال في ساحتها",
  "open work nearby; the stand is still usable": "عمل مفتوح قريب؛ والموقف ما زال صالحاً",
  "conflicts in the plan": "تعارضات في الخطة",
  "in what is already assigned, not in a proposal": "فيما هو مُسند بالفعل، لا في اقتراح",
  "the plan is clean": "الخطة سليمة",
  "movements with no stand": "حركات بلا موقف",
  "each one needs a place to park": "كل منها تحتاج مكاناً للوقوف",
  "every movement has a stand": "لكل حركة موقف",
  "Every movement has a stand.": "لكل حركة موقف.",
  "stand use": "استخدام المواقف",
  "of the usable stand-hours in the day": "من ساعات المواقف الصالحة في اليوم",
  "the stand plan": "خطة المواقف",
  "the day's schedule": "جدول اليوم",
  "Every stand is open.": "كل المواقف مفتوحة.",
  "The plan has no conflicts.": "لا تعارضات في الخطة.",
  "Nothing is planned on any stand today.": "لا شيء مخطط على أي موقف اليوم.",
  "cannot be used with {list}": "لا يمكن استخدامه مع {list}",
  "{n} conflict|{n} conflicts": "لا تعارض|تعارض واحد|تعارضان|{n} تعارضات|{n} تعارضاً|{n} تعارض",
  "{n} conflict stands in the plan.|{n} conflicts stand in the plan.":
    "لا تعارضات في الخطة.|تعارض واحد قائم في الخطة.|تعارضان قائمان في الخطة.|{n} تعارضات قائمة في الخطة.|{n} تعارضاً قائمة في الخطة.|{n} تعارض قائم في الخطة.",
  "{n} stand is closed by work elsewhere on the platform.|{n} stands are closed by work elsewhere on the platform.":
    "لا مواقف مغلقة.|موقف واحد مغلق بعمل في مكان آخر على المنصة.|موقفان مغلقان بعمل في مكان آخر على المنصة.|{n} مواقف مغلقة بعمل في مكان آخر على المنصة.|{n} موقفاً مغلقة بعمل في مكان آخر على المنصة.|{n} موقف مغلق بعمل في مكان آخر على المنصة.",
  "{n} movement has nowhere to park.|{n} movements have nowhere to park.":
    "لكل حركة مكان.|حركة واحدة بلا مكان للوقوف.|حركتان بلا مكان للوقوف.|{n} حركات بلا مكان للوقوف.|{n} حركة بلا مكان للوقوف.|{n} حركة بلا مكان للوقوف.",
  "{site} has {n}, {usable} of them usable today.": "{site} به {n}، منها {usable} صالحة اليوم.",

  /* the four refusals */
  "{stand} is not a stand on this airport.": "{stand} ليس موقفاً في هذا المطار.",
  "{stand} is taken by {flight} from {from} to {to}.": "{stand} مشغول بـ{flight} من {from} إلى {to}.",
  "{stand} takes code {max}; {flight} is code {size}.": "{stand} يستوعب الرمز {max}؛ و{flight} من الرمز {size}.",
  "{stand} cannot be used while {other} is occupied, and {flight} is on it.":
    "{stand} لا يمكن استخدامه ما دام {other} مشغولاً، و{flight} عليه.",
  "{stand} is out of service: {why}": "{stand} خارج الخدمة: {why}",
  "{stand} is closed by {kind} {ref}: {why}": "{stand} مغلق بـ{kind} {ref}: {why}",
  "work order": "أمر عمل",
  "{title} — the order names this stand": "{title} — الأمر يسمي هذا الموقف",
  "{title} — open work on {zone}": "{title} — عمل مفتوح على {zone}",
  "the surface it stands on is closed": "السطح الذي يقف عليه مغلق",
  "What is wrong with the plan": "ما الخلل في الخطة",
  "found in what is already assigned": "موجود فيما هو مُسند بالفعل",
  "The board": "اللوحة",
  "every stand, what is on it, and what is closing it": "كل موقف، وما عليه، وما يغلقه",
  "Where every movement parks, and everything wrong with the plan as it stands — not only with a change somebody is making now. A stand can be wrong in four ways: something else is on it, the aircraft is too big for it, the neighbour it cannot share with is occupied, or the rest of the platform has closed it.":
    "أين تقف كل حركة، وكل خلل في الخطة كما هي — لا في تغيير يجريه أحدهم الآن فقط. والموقف يخطئ بأربع صور: شيء آخر عليه، أو الطائرة أكبر منه، أو الموقف المجاور الذي لا يشاركه مشغول، أو بقية المنصة أغلقته.",
  "The closures are not typed here. This screen reads the same work orders every other screen reads: an order that NAMES a stand takes it out of the allocation, and one that is merely open on the same apron is shown as work nearby rather than treated as a closure — a marking repaint on one stand does not close the apron it sits on. A surface is closed by a closure.":
    "الإغلاقات لا تُكتب هنا. هذه الشاشة تقرأ أوامر العمل نفسها التي تقرأها كل شاشة أخرى: الأمر الذي يسمي موقفاً يُخرجه من التوزيع، والأمر المفتوح على الساحة نفسها يُعرض كعمل قريب لا كإغلاق — فإعادة دهان علامات على موقف واحد لا تغلق الساحة التي يقف عليها. والسطح يُغلقه إغلاق.",

  /* where else it could go */
  "Movements with nowhere to park": "حركات بلا مكان للوقوف",
  "ask the board where each one could go": "اسأل اللوحة أين يمكن أن تقف كل منها",
  "Where can it go?": "أين يمكن أن تقف؟",
  "Where {flight} could go": "أين يمكن أن تقف {flight}",
  "a contact stand, and exactly the size this aircraft needs": "موقف ملاصق، وبالمقاس الذي تحتاجه هذه الطائرة تماماً",
  "a contact stand; {n} larger than this aircraft needs": "موقف ملاصق؛ أكبر بـ{n} مما تحتاجه هذه الطائرة",
  "{n} size|{n} sizes": "لا فروق|مقاس واحد|مقاسين|{n} مقاسات|{n} مقاساً|{n} مقاس",
  "{kind}; passengers are bussed": "{kind}؛ يُنقل المسافرون بالحافلات",
  "A contact stand before a remote one, because passengers walk; then the smallest stand the aircraft fits on, because putting a narrowbody on a widebody stand spends the only stand a widebody can use. The stands that will not work are listed too, with the reason — because 'everything is full' is an answer somebody needs to see the shape of.":
    "الموقف الملاصق قبل البعيد، لأن المسافرين يمشون؛ ثم أصغر موقف تتسع له الطائرة، لأن وضع طائرة ضيقة البدن على موقف عريض البدن يستهلك الموقف الوحيد الذي تصلح له العريضة. وتُدرج المواقف غير الصالحة أيضاً مع السبب — لأن «كل شيء ممتلئ» جواب يحتاج المرء أن يرى شكله.",

  /* ── turnarounds, in A-CDM milestones (2026-09-21) ─────────────────────
   *
   * On the terms: التزام is the handler's commitment (TOBT) and is
   * deliberately not "تقدير" (an estimate) anywhere — the whole method rests
   * on the two being different things.
   */
  Turnarounds: "دورات المناولة",
  Turnaround: "دورة المناولة",
  "turnarounds today": "دورات المناولة اليوم",
  "{n} turnaround|{n} turnarounds": "لا دورات|دورة واحدة|دورتان|{n} دورات|{n} دورة|{n} دورة",
  "{n} away already": "{n} غادرت بالفعل",
  "cannot make their commitment": "لا تستطيع الوفاء بالتزامها",
  "on stand too late for the minimum turnaround": "وصلت الموقف متأخرة عن الحد الأدنى للمناولة",
  "every aircraft on stand has time in hand": "كل طائرة على موقف لديها وقت كافٍ",
  "Every aircraft on stand has time in hand.": "كل طائرة على موقف لديها وقت كافٍ.",
  "committed time already passed": "الوقت الملتزم به مضى",
  "still on stand, and the word given has gone by": "ما زالت على الموقف، وقد مضى الوقت الملتزم به",
  "none has passed": "لم يمضِ أي منها",
  "No committed off-block time has passed.": "لم يمضِ أي وقت مغادرة موقف ملتزم به.",
  "commitments kept": "الالتزامات المحققة",
  "of {n} that have pushed back": "من {n} غادرت الموقف",
  "nothing has pushed back yet": "لم تغادر أي طائرة الموقف بعد",
  "On stand": "على الموقف",
  "Committed off-block": "المغادرة الملتزم بها",
  "Off blocks": "غادرت الموقف",
  "Ground time": "الوقت الأرضي",
  "min {n}": "الحد الأدنى {n}",
  derived: "مشتق",
  "{n} late off blocks": "{n} تأخير في مغادرة الموقف",
  "Not on stand": "ليست على موقف",
  "Cannot make it": "لن تلحق",
  "Commitment passed": "مضى الالتزام",
  Moving: "تتحرك",
  Away: "غادرت",
  "The turnarounds": "دورات المناولة",
  "worst first — what a duty manager reads this board to find":
    "الأسوأ أولاً — وهو ما يقرأ مدير المناوبة هذه اللوحة ليجده",
  "No movement on the day is paired into a turnaround.": "لا حركة في اليوم مقترنة في دورة مناولة.",
  "What will not make it": "ما لن يلحق",
  "said in full, from the record rather than from a feeling": "مذكور بالكامل، من السجل لا من انطباع",
  "{site}: {n} on the day.": "{site}: {n} في اليوم.",
  "{n} cannot make the off-block time committed to.|{n} cannot make the off-block times committed to.":
    "لا شيء متأخر.|واحدة لا تستطيع الوفاء بوقت المغادرة الملتزم به.|اثنتان لا تستطيعان الوفاء بأوقات المغادرة الملتزم بها.|{n} لا تستطيع الوفاء بأوقات المغادرة الملتزم بها.|{n} لا تستطيع الوفاء بأوقات المغادرة الملتزم بها.|{n} لا تستطيع الوفاء بوقت المغادرة الملتزم به.",
  "{n} is still on stand past its committed time.|{n} are still on stand past their committed time.":
    "لا شيء.|واحدة ما زالت على الموقف بعد وقتها الملتزم به.|اثنتان ما زالتا على الموقف بعد وقتهما الملتزم به.|{n} ما زالت على الموقف بعد أوقاتها الملتزم بها.|{n} ما زالت على الموقف بعد أوقاتها الملتزم بها.|{n} ما زالت على الموقف بعد وقتها الملتزم به.",
  "{n} minute|{n} minutes": "لا دقائق|دقيقة واحدة|دقيقتان|{n} دقائق|{n} دقيقة|{n} دقيقة",
  "{flight} has not arrived on stand yet.": "{flight} لم تصل إلى الموقف بعد.",
  "{flight} is on stand and has time in hand.": "{flight} على الموقف ولديها وقت كافٍ.",
  "{flight} is away.": "{flight} غادرت.",
  "{flight} pushed back on the handler's own commitment.": "{flight} غادرت الموقف في الوقت الذي التزمت به المناولة.",
  "{flight} pushed back {n} after the time the handler committed to.":
    "{flight} غادرت الموقف بعد {n} من الوقت الذي التزمت به المناولة.",
  "{flight} has not moved and the committed off-block time has passed.":
    "{flight} لم تتحرك وقد مضى وقت المغادرة الملتزم به.",
  "{flight} cannot make its committed off-block time: it came on stand at {aibt} and a turnaround of this kind takes at least {min}.":
    "{flight} لا تستطيع الوفاء بوقت مغادرتها الملتزم به: وصلت الموقف في {aibt}، ودورة مناولة من هذا النوع تستغرق {min} على الأقل.",
  "The ground time between an arrival and its departure, in the milestones A-CDM agrees on. The target off-block time is the handler's COMMITMENT, not a prediction: air traffic allocates a start-up slot against that word, so this board never quietly revises one. It says whether it could be met when it was made, whether it has passed, and whether it was met.":
    "الوقت الأرضي بين الوصول ومغادرته، بالمحطات التي يتفق عليها A-CDM. ووقت مغادرة الموقف المستهدف التزامٌ من المناولة لا تنبؤاً: تخصص الحركة الجوية فتحة تشغيل مقابل تلك الكلمة، فلا تراجع هذه اللوحة التزاماً بصمت أبداً. وتقول هل كان ممكناً حين قُطع، وهل مضى، وهل تحقق.",
  "{n} of these have no committed time recorded, so the scheduled off-block time is shown instead and marked as derived. A time nobody committed to is not a commitment, and the board says which is which.":
    "{n} منها بلا وقت التزام مسجل، فيُعرض وقت المغادرة المجدول بدلاً منه ويُعلَّم كمشتق. والوقت الذي لم يلتزم به أحد ليس التزاماً، واللوحة تبيّن أيهما أي.",
  "Every turnaround here carries a committed off-block time somebody actually gave.":
    "كل دورة مناولة هنا تحمل وقت مغادرة التزم به أحدهم فعلاً.",

  /* ── the public board ─────────────────────────────────────────────── */
  "Flight information": "معلومات الرحلات",
  "Departures or arrivals": "المغادرة أو الوصول",
  "to follow": "يُعلن لاحقاً",
  "by bus from the gate": "بالحافلة من البوابة",
  Boarding: "جارٍ الصعود",
  "Final call": "النداء الأخير",
  "Gate closed": "أُغلقت البوابة",
  Departed: "غادرت",
  Landed: "هبطت",
  Arrived: "وصلت",
  "Please ask staff": "يرجى سؤال الموظفين",

  /* the public board's own words: displays, cities, desks, belts, the hall line */
  Belt: "السير",
  "Baggage reclaim": "استلام الأمتعة",
  "Welcome to {city}": "أهلاً بكم في {city}",
  "{flight} to {city} is boarding at gate {gate}.": "الرحلة {flight} إلى {city} يجري الصعود إليها من البوابة {gate}.",
  "Final call for {flight} to {city} at gate {gate}.": "النداء الأخير للرحلة {flight} إلى {city} عند البوابة {gate}.",
  "{flight} to {city} is cancelled. Please contact your airline.":
    "أُلغيت الرحلة {flight} إلى {city}. يرجى التواصل مع شركة الطيران.",
  "For {flight} please ask a member of staff.": "للاستفسار عن الرحلة {flight} يرجى سؤال أحد الموظفين.",
  "{flight} to {city} is delayed. A new time will follow.": "تأخرت الرحلة {flight} إلى {city}. يُعلن الموعد الجديد لاحقاً.",
  "{flight} from {city} has landed. Baggage on {belt}.": "هبطت الرحلة {flight} القادمة من {city}. الأمتعة على {belt}.",
  "Check-in for {flight} to {city} is open at desks {desks}.":
    "إنهاء إجراءات الرحلة {flight} إلى {city} مفتوح عند الكاونترات {desks}.",
  "Gate closes in {n} min": "تُغلق البوابة خلال {n} دقيقة",
  "Next at this gate": "التالي عند هذه البوابة",
  "No departure is due at this gate.": "لا مغادرة مستحقة عند هذه البوابة.",
  "No flight is due on this belt.": "لا رحلة مستحقة على هذا السير.",
  "Landed {time}": "هبطت {time}",
  "Expected {time}": "متوقعة {time}",
  "Page {n} of {of}": "الصفحة {n} من {of}",
  "Gate {gate}": "البوابة {gate}",
  "Which display": "أي شاشة",
  "Hall board": "لوحة الصالة",
  "Gate screen": "شاشة البوابة",
  "Belt screen": "شاشة السير",
  "Board language": "لغة اللوحة",
  "Arabic and English by turns": "العربية والإنجليزية بالتناوب",
  "Show on a wall screen": "اعرضها على شاشة حائط",
  "Leave the wall screen": "اخرج من شاشة الحائط",
  "{city} show a code instead of a city, {desks} have no check-in desks in the plan and {belt} no belt.":
    "{city} تعرض رمزاً بدل اسم المدينة، و{desks} بلا كاونترات إنهاء إجراءات في الخطة، و{belt} بلا سير.",
  "{n} shows a code instead of a city.|{n} show a code instead of a city.":
    "لا شيء يعرض رمزاً بدل المدينة.|حركة واحدة تعرض رمزاً بدل المدينة.|حركتان تعرضان رمزاً بدل المدينة.|{n} حركات تعرض رمزاً بدل المدينة.|{n} حركة تعرض رمزاً بدل المدينة.|{n} حركة تعرض رمزاً بدل المدينة.",
  "{n} departure due soon has no check-in desks in the plan.|{n} departures due soon have no check-in desks in the plan.":
    "لا مغادرة قريبة بلا كاونترات.|مغادرة قريبة واحدة بلا كاونترات في الخطة.|مغادرتان قريبتان بلا كاونترات في الخطة.|{n} مغادرات قريبة بلا كاونترات في الخطة.|{n} مغادرة قريبة بلا كاونترات في الخطة.|{n} مغادرة قريبة بلا كاونترات في الخطة.",
  "{n} arrival has no belt in the plan.|{n} arrivals have no belt in the plan.":
    "لا وصول بلا سير.|وصول واحد بلا سير في الخطة.|وصولان بلا سير في الخطة.|{n} وصولات بلا سير في الخطة.|{n} وصولاً بلا سير في الخطة.|{n} وصول بلا سير في الخطة.",
  "a code, not a city: {code} is not in the city list": "رمز لا مدينة: {code} ليس في قائمة المدن",
  "no check-in desks in the allocation plan": "لا كاونترات إنهاء إجراءات في خطة التخصيص",
  "no reclaim belt in the allocation plan": "لا سير أمتعة في خطة التخصيص",
  "Check-in desks and reclaim belts are read from the allocation plan, never typed on the board; a flight the plan has not placed shows none. Cities come from the board's city list — a code that is not in it is printed as it is, never guessed.":
    "تُقرأ كاونترات إنهاء الإجراءات وسيور الأمتعة من خطة التخصيص ولا تُكتب على اللوحة أبداً؛ والرحلة التي لم تخصصها الخطة لا يظهر لها شيء. وتأتي المدن من قائمة مدن اللوحة — والرمز غير الموجود فيها يُطبع كما هو ولا يُخمَّن أبداً.",
  "Nothing is due in the next few hours.": "لا شيء مستحق في الساعات القليلة القادمة.",
  "What the board is not telling anybody": "ما لا تخبر به اللوحة أحداً",
  "the same movements, read back as the questions a passenger would have to ask a person":
    "الحركات نفسها، مقروءة كأسئلة سيضطر المسافر لطرحها على شخص",
  "What is missing": "ما الناقص",
  "no time a passenger can use": "لا وقت يفيد المسافر",
  "no gate and no bus point": "لا بوابة ولا نقطة حافلات",
  "nothing but 'ask staff'": "لا شيء سوى «اسأل الموظفين»",
  "Every movement on the board carries a time, a gate and a status.": "كل حركة على اللوحة تحمل وقتاً وبوابة وحالة.",
  "{n} movement has no time on the board.|{n} movements have no time on the board.":
    "لا شيء بلا وقت.|حركة واحدة بلا وقت على اللوحة.|حركتان بلا وقت على اللوحة.|{n} حركات بلا وقت على اللوحة.|{n} حركة بلا وقت على اللوحة.|{n} حركة بلا وقت على اللوحة.",
  "{n} departure has no gate.|{n} departures have no gate.":
    "لا مغادرة بلا بوابة.|مغادرة واحدة بلا بوابة.|مغادرتان بلا بوابة.|{n} مغادرات بلا بوابة.|{n} مغادرة بلا بوابة.|{n} مغادرة بلا بوابة.",
  "{n} tells the passenger only to ask staff.|{n} tell the passenger only to ask staff.":
    "لا شيء.|واحدة لا تقول للمسافر سوى أن يسأل الموظفين.|اثنتان لا تقولان للمسافر سوى أن يسأل الموظفين.|{n} لا تقول للمسافر سوى أن يسأل الموظفين.|{n} لا تقول للمسافر سوى أن يسأل الموظفين.|{n} لا تقول للمسافر سوى أن يسأل الموظفين.",
  "{site}: {n} on the public board.": "{site}: {n} على اللوحة العامة.",
  "{noTime} carry no time a passenger can use, and {ask} say only that somebody should ask staff.":
    "{noTime} بلا وقت يفيد المسافر، و{ask} لا تقول سوى أن يسأل أحدهم الموظفين.",
  "What a passenger sees, and beneath it what it is failing to tell them. Every status here is derived from the movement's own record — there is no way to type one — so the board cannot disagree with the operation. A movement that is late with no new time shows no time at all, because a board still displaying a time that has passed is worse than one admitting it does not know yet.":
    "ما يراه المسافر، وتحته ما تعجز اللوحة عن إخباره به. كل حالة هنا مشتقة من سجل الحركة نفسه — ولا سبيل لكتابة واحدة — فلا يمكن للوحة أن تخالف التشغيل. والحركة المتأخرة بلا وقت جديد لا تُظهر وقتاً على الإطلاق، لأن لوحة ما زالت تعرض وقتاً مضى أسوأ من لوحة تعترف بأنها لا تعرف بعد.",
  "A cancellation shows as cancelled and never shows its reason: why an aircraft is not flying is between the operator and the airline, and a display is read by twelve thousand people who cannot ask a follow-up question.":
    "الإلغاء يظهر كإلغاء ولا يُظهر سببه أبداً: سبب عدم طيران الطائرة شأن بين المشغّل وشركة الطيران، واللوحة يقرأها اثنا عشر ألف شخص لا يستطيعون طرح سؤال إضافي.",
  "—": "—",
  "stand {s}": "الموقف {s}",
  "gate {g}": "البوابة {g}",
  "{n} movements delayed today, {c} still to come.": "{n} رحلات متأخرة اليوم، و{c} لم تصل أو تغادر بعد.",
  "{e} came with early warning; {x} reached us after the scheduled time or not at all.":
    "{e} جاءت بإنذار مبكر؛ و{x} وصلتنا بعد الموعد المجدول أو لم تصلنا أبداً.",
  "{n} knock-ons on stands and gates.": "{n} آثار متتابعة على المواقف والبوابات.",
  "Which delays hit the stand plan?": "أي التأخيرات أثّرت على خطة المواقف؟",
  "delayed today": "متأخرة اليوم",
  "with early warning": "بإنذار مبكر",
  "an hour or more before the scheduled time": "قبل الموعد المجدول بساعة أو أكثر",
  "at short notice": "بإشعار قصير",
  "less than an hour before": "قبل أقل من ساعة",
  "after the fact or never": "بعد الموعد أو لم يصل",
  "told after the scheduled time, or not told": "أُبلغنا بعد الموعد المجدول، أو لم نُبلَّغ",
  "warning, on average": "مدة الإنذار في المتوسط",
  "between the first notice and the scheduled time": "بين أول إشعار والموعد المجدول",
  "knock-ons": "الآثار المتتابعة",
  "on stands and gates": "على المواقف والبوابات",
  "Every delay as it reached the airport — from whom, when, and with what new time — and what it does to the stands, aprons and gates planned after it.":
    "كل تأخير كما وصل إلى المطار — ممن ومتى وبأي موعد جديد — وما يفعله بالمواقف والساحات والبوابات المخططة بعده.",
  "the day's movements and their delay notices": "حركات اليوم وإشعارات تأخيرها",
  "Still to come only": "التي لم تتم بعد فقط",
  "No delayed movement matches.": "لا توجد حركة متأخرة مطابقة.",
  actual: "فعلي",
  "first told {m} min before, by {src}": "أول إبلاغ قبل {m} دقيقة، من {src}",
  "first told {m} min after the scheduled time, by {src}": "أول إبلاغ بعد الموعد المجدول بـ{m} دقيقة، من {src}",
  "new time {t}": "الموعد الجديد {t}",
  "Nobody told the airport; the delay was seen, not announced.": "لم يُبلغ أحد المطار؛ رُصد التأخير ولم يُعلن عنه.",
  "{f}: new time {t} recorded, from {src}.": "{f}: سُجّل الموعد الجديد {t}، من {src}.",
  "Choose a movement": "اختر حركة",
  "e.g. SV operations control": "مثال: مركز عمليات السعودية",
  "Record the notice": "سجّل الإشعار",
  "What the colors mean": "دلالة الألوان",
  "local time": "التوقيت المحلي",
  "Move {victim} to stand {stand}: {because}.": "انقل {victim} إلى الموقف {stand}: {because}.",
  "No free stand fits; hold the arrival or re-plan the apron.":
    "لا يوجد موقف شاغر مناسب؛ أوقف القادمة أو أعد تخطيط الساحة.",
  "Give one of them another gate, or bus from a remote stand.":
    "أعطِ إحداهما بوابة أخرى، أو انقل الركاب بالحافلة من موقف بعيد.",
  "Doors late": "الأبواب متأخرة",
  "{n} min past door close": "تجاوزت إغلاق الأبواب بـ{n} دقيقة",
  "told by {src}": "أبلغ عنه {src}",
  "{cause} now holds stand {where} until {until}, {m} minutes into {victim}'s slot.":
    "تشغل {cause} الآن الموقف {where} حتى {until}، أي {m} دقيقة من موعد {victim}.",
  "{cause} now boards at gate {where} until {until}, {m} minutes into {victim}'s boarding.":
    "تصعد ركاب {cause} الآن من البوابة {where} حتى {until}، أي {m} دقيقة من صعود {victim}.",
  "Airline operations control": "مركز عمليات شركة الطيران",
  "Ground handler": "مقدّم الخدمات الأرضية",
  "ATC flow message": "رسالة تنظيم الحركة الجوية",
  "Schedule feed": "موجز الجداول",
  "Airport duty manager": "مدير المناوبة في المطار",
  "Early warning": "إنذار مبكر",
  "Short notice": "إشعار قصير",
  "After the scheduled time": "بعد الموعد المجدول",
  "No notice received": "لم يصل إشعار",
  Delayed: "متأخرة",
  Delays: "التأخيرات",
  "Knock-on on stands and gates": "الأثر على المواقف والبوابات",
  "the plan as the delays now make it": "الخطة كما تجعلها التأخيرات الآن",
  "No delay is running into another movement's stand or gate.": "لا يتعارض أي تأخير مع موقف أو بوابة حركة أخرى.",
  "Delayed movements, and who told us": "الحركات المتأخرة ومن أبلغنا",
  "the first notice decides the warning; every revision is kept": "أول إشعار يحدد مدة الإنذار؛ وتُحفظ كل مراجعة",
  Warning: "الإنذار",
  "Where the notices came from": "مصادر الإشعارات",
  "how many notices each source sent today": "عدد الإشعارات التي أرسلها كل مصدر اليوم",
  "Record a delay notice": "تسجيل إشعار تأخير",
  "when a source tells somebody by phone or message; it becomes the movement's new estimate":
    "حين يُبلغ مصدرٌ أحداً بالهاتف أو برسالة؛ ويصبح الموعد التقديري الجديد للحركة",
  Movement: "الحركة",
  "New time (local)": "الموعد الجديد (محلي)",
  "Knock-on from today's delays": "أثر تأخيرات اليوم",
  "stands held longer and gates boarding later than planned, with a free stand for whoever is pushed out":
    "مواقف مشغولة أطول وبوابات تتأخر في الصعود عن المخطط، مع موقف شاغر لمن يُزاح",
  "doors late": "أبواب متأخرة",
  "boarding now": "جارٍ الصعود",
  "still to come": "لم تتم بعد",
  "Every delayed movement with who reported it — airline operations control, ground handler, ATC flow message, schedule feed or duty manager — when, the new time and each revision, how much warning that gave, and what the delay does to the stands, aprons and gates planned after it, with a free stand for whoever is pushed out; a notice can be recorded when a source phones one in.":
    "كل حركة متأخرة مع من أبلغ عنها — مركز عمليات شركة الطيران أو مقدّم الخدمات الأرضية أو رسالة تنظيم الحركة الجوية أو موجز الجداول أو مدير المناوبة — ومتى، والموعد الجديد وكل مراجعة، ومدة الإنذار، وما يفعله التأخير بالمواقف والساحات والبوابات المخططة بعده، مع موقف شاغر لمن يُزاح؛ ويمكن تسجيل إشعار حين يُبلغ مصدرٌ به هاتفياً.",
  "from {p}": "من {p}",
  "to {p}": "إلى {p}",
  "No stands in this part of the map.": "لا توجد مواقف في هذا الجزء من الخريطة.",
  "Back to the stands": "العودة إلى المواقف",

  /* one flight world: the record and the allocation plan underneath the screens */
  "Placed by hand": "وُضعت يدوياً",
  "Allocation engine": "محرك التخصيص",
  "Stand plan": "خطة المواقف",
  Cancelled: "ملغاة",
  "That flight is not on the record.": "هذه الرحلة غير موجودة في السجل.",
  "{flights} not on the stand plan yet, so not placed.": "{flights} ليست في خطة المواقف بعد، فلم تُوضع.",
  "No flights on the record for {date}.": "لا توجد رحلات في السجل ليوم {date}.",
  "Add the schedule": "أضف الجدول",
  "Not saved: {why}": "لم يُحفظ: {why}",
  "Undoing…": "جارٍ التراجع…",
  "Undone.": "تم التراجع.",
  "Could not undo: {why}": "تعذّر التراجع: {why}",
  "The stand plan is not available, so nothing was saved.": "خطة المواقف غير متاحة، فلم يُحفظ شيء.",
  "The stand plan could not be read, so stands, gates and sizes are not shown: {why}":
    "تعذّرت قراءة خطة المواقف، فلا تظهر المواقف والبوابات والأحجام: {why}",
  "Could not refresh the flight record, so this is the last good read: {why}":
    "تعذّر تحديث سجل الرحلات، وهذه آخر قراءة سليمة: {why}",
};

export default ar;
